/**
 * Header Menu Component
 * Displays icons and profile button with redirect to profile page
 * @component HeaderMenu
 */

import React, { useEffect, useState, useRef } from 'react';
import LogoutIcon from '@mui/icons-material/Logout';
import { Badge, Tooltip } from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigation } from '../../../hooks/useNavigation';
import { useLanguage } from '../../../contexts/LanguageContext';
import { translate } from '../../../locales';
import SupportChatModal from '../../../modals/SupportChatModal/SupportChatModal';
import webSocketService from '../../../services/websocket';
import './style.css';

interface HeaderMenuProps {
  onLoginClick?: () => void;
}

const HeaderMenu: React.FC<HeaderMenuProps> = ({ onLoginClick }) => {
  const { navigateToPrivate } = useNavigation();
  const { user, logout, isAuthenticated } = useAuth();
  const { language } = useLanguage();
  const webSocketInitialized = useRef(false);

  const [isSupportChatOpen, setIsSupportChatOpen] = useState(false);
  const [supportMessage, setSupportMessage] = useState<any>(null);
  const [unreadSupportMessagesCount, setUnreadSupportMessagesCount] = useState(0);
  const [unreadClientMessagesForAdmin, setUnreadClientMessagesForAdmin] = useState(0);

  const getSupportLastSeenStorageKey = () => {
    const userId = user?.id || 'anonymous';
    return `support_last_seen_at_${userId}`;
  };

  const getAdminLastSeenStorageKey = () => {
    return `admin_support_last_seen_at`;
  };

  const readSupportLastSeenAt = () => {
    try {
      const key = getSupportLastSeenStorageKey();
      const raw = localStorage.getItem(key);
      if (!raw) return 0;
      const t = new Date(raw).getTime();
      return Number.isFinite(t) ? t : 0;
    } catch {
      return 0;
    }
  };

  const readAdminLastSeenAt = () => {
    try {
      const key = getAdminLastSeenStorageKey();
      const raw = localStorage.getItem(key);
      if (!raw) return 0;
      const t = new Date(raw).getTime();
      return Number.isFinite(t) ? t : 0;
    } catch {
      return 0;
    }
  };

  const writeSupportLastSeenNow = () => {
    try {
      const key = getSupportLastSeenStorageKey();
      localStorage.setItem(key, new Date().toISOString());
    } catch {
      // ignore
    }
  };

  const writeAdminLastSeenNow = () => {
    try {
      const key = getAdminLastSeenStorageKey();
      localStorage.setItem(key, new Date().toISOString());
    } catch {
      // ignore
    }
  };

  const getLatestAdminMessageTime = (m: any) => {
    let latest = 0;

    const respondedAt = m?.responded_at?.Valid ? m?.responded_at?.String : undefined;
    if (respondedAt) {
      const t = new Date(respondedAt).getTime();
      if (Number.isFinite(t)) latest = Math.max(latest, t);
    }

    if (Array.isArray(m?.conversation)) {
      for (const msg of m.conversation) {
        if (msg?.sender_type !== 'admin') continue;
        const t = new Date(msg?.created_at).getTime();
        if (Number.isFinite(t)) latest = Math.max(latest, t);
      }
    }

    // Fallback for very old legacy threads: if there is an admin_response but no responded_at,
    // use updated_at as a best-effort indicator.
    if (latest === 0 && m?.admin_response?.Valid) {
      const t = new Date(m?.updated_at || m?.created_at).getTime();
      if (Number.isFinite(t)) latest = Math.max(latest, t);
    }

    return latest;
  };

  const countUnreadSupportMessagesByLastSeen = (messages: any[]) => {
    if (!Array.isArray(messages) || messages.length === 0) return 0;
    const lastSeen = readSupportLastSeenAt();
    let count = 0;
    for (const m of messages) {
      const latestAdminTime = getLatestAdminMessageTime(m);
      if (latestAdminTime > lastSeen) {
        // Count how many admin messages are newer than lastSeen
        if (Array.isArray(m?.conversation)) {
          for (const msg of m.conversation) {
            if (msg?.sender_type === 'admin' && new Date(msg?.created_at).getTime() > lastSeen) {
              count++;
            }
          }
        } else {
          // Legacy fallback: count at most 1 for responded_at
          if (latestAdminTime > lastSeen) count++;
        }
      }
    }
    return count;
  };

  const countUnreadClientMessagesForAdmin = (messages: any[]) => {
    if (!Array.isArray(messages) || messages.length === 0) return 0;
    const adminLastSeen = readAdminLastSeenAt();
    let count = 0;
    for (const m of messages) {
      // Count client follow-up messages newer than admin last seen
      if (Array.isArray(m?.conversation)) {
        for (const msg of m.conversation) {
          if (msg?.sender_type === 'client' && new Date(msg?.created_at).getTime() > adminLastSeen) {
            count++;
          }
        }
      }
    }
    return count;
  };

  const getLatestActiveSupportMessage = (messages: any[]) => {
    if (!Array.isArray(messages) || messages.length === 0) return null;

    const getTime = (m: any) => {
      const ts = m?.updated_at || m?.created_at;
      const t = ts ? new Date(ts).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    };

    return messages.reduce((latest: any, current: any) => {
      return getTime(current) >= getTime(latest) ? current : latest;
    }, messages[0]);
  };

  const t = (key: any) => translate(key, language as 'en' | 'ua');

  const handleProfileClick = () => {
    navigateToPrivate('PROFILE');
  };

  const fetchSupportMessages = async () => {
    const isAdmin = user?.role === 'super_admin';
    const endpoint = isAdmin ? '/api/v1/admin/support/messages' : '/api/v1/support/messages';
    const listResponse = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!listResponse.ok) {
      throw new Error('Failed to fetch support messages');
    }

    const list = await listResponse.json();
    const messages = list?.messages || [];
    return messages;
  };

  // WebSocket connection and message handling
  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadSupportMessagesCount(0);
      setUnreadClientMessagesForAdmin(0);
      webSocketService.disconnect();
      webSocketInitialized.current = false;
      return;
    }

    // Prevent multiple initializations
    if (webSocketInitialized.current) {
      return;
    }

    // Initial fetch of unread counts
    const fetchInitialCounts = async () => {
      try {
        const messages = await fetchSupportMessages();
        if (user?.role === 'super_admin') {
          setUnreadSupportMessagesCount(0);
          setUnreadClientMessagesForAdmin(countUnreadClientMessagesForAdmin(messages));
        } else {
          setUnreadSupportMessagesCount(countUnreadSupportMessagesByLastSeen(messages));
          setUnreadClientMessagesForAdmin(0);
        }
      } catch (error) {
        console.error('Failed to fetch initial support messages:', error);
      }
    };

    // Setup WebSocket event handlers
    const handleNewMessage = (message: any) => {
      console.log('[HeaderMenu] New support message:', message);
      if (user?.role === 'super_admin') {
        // Increment unread client messages for admin
        setUnreadClientMessagesForAdmin(prev => prev + 1);
      }
    };

    const handleNewReply = (message: any) => {
      console.log('[HeaderMenu] New support reply:', message);
      console.log('[HeaderMenu] User role:', user?.role);
      console.log('[HeaderMenu] Sender role:', message.data?.sender_role);
      
      if (user?.role === 'super_admin') {
        // Admin sent reply, no change needed for admin
        console.log('[HeaderMenu] Admin sent reply - no counter change');
      } else {
        // Client received admin reply
        console.log('[HeaderMenu] Client received admin reply - incrementing counter');
        setUnreadSupportMessagesCount(prev => {
          console.log('[HeaderMenu] Previous count:', prev, 'New count:', prev + 1);
          return prev + 1;
        });
      }
    };

    const handleUnreadCount = (message: any) => {
      console.log('[HeaderMenu] Unread count update:', message);
      if (message.data.is_admin) {
        setUnreadClientMessagesForAdmin(message.data.unread_count || 0);
      } else {
        setUnreadSupportMessagesCount(message.data.unread_count || 0);
      }
    };

    const handleMessageUpdate = (message: any) => {
      console.log('[HeaderMenu] Message update:', message);
      // Refresh counts when message status changes
      fetchInitialCounts();
    };

    // Connect to WebSocket
    webSocketInitialized.current = true;
    webSocketService.connect()
      .then(() => {
        console.log('[HeaderMenu] WebSocket connected successfully');
        
        // Add event listeners
        webSocketService.addEventListener('support_new_message', handleNewMessage);
        webSocketService.addEventListener('support_new_reply', handleNewReply);
        webSocketService.addEventListener('support_unread_count', handleUnreadCount);
        webSocketService.addEventListener('support_message_update', handleMessageUpdate);
        
        // Fetch initial counts after connection
        fetchInitialCounts();
      })
      .catch(error => {
        console.error('[HeaderMenu] WebSocket connection failed:', error);
        webSocketInitialized.current = false;
        // Fallback to polling if WebSocket fails
        fetchInitialCounts();
      });

    // Cleanup
    return () => {
      webSocketService.removeEventListener('support_new_message', handleNewMessage);
      webSocketService.removeEventListener('support_new_reply', handleNewReply);
      webSocketService.removeEventListener('support_unread_count', handleUnreadCount);
      webSocketService.removeEventListener('support_message_update', handleMessageUpdate);
    };
  }, [isAuthenticated, user?.role, user?.id]);

  const handleSupportChatClick = async () => {
    if (!isAuthenticated) {
      onLoginClick?.();
      return;
    }

    if (user?.role === 'super_admin') {
      // Try to open the latest unread client message in a modal
      try {
        const messages = await fetchSupportMessages();
        const adminLastSeen = readAdminLastSeenAt();
        const unreadMessages = messages.filter((m: any) => {
          if (Array.isArray(m?.conversation)) {
            return m.conversation.some((msg: any) => msg?.sender_type === 'client' && new Date(msg?.created_at).getTime() > adminLastSeen);
          }
          return false;
        });
        if (unreadMessages.length > 0) {
          setSupportMessage(unreadMessages[0]);
          setIsSupportChatOpen(true);
          writeAdminLastSeenNow();
          setUnreadClientMessagesForAdmin(0);
          return;
        }
      } catch (error) {
        console.error('Failed to fetch unread messages for modal:', error);
      }
      // Fallback: navigate to admin support page
      navigateToPrivate('ADMIN_SUPPORT');
      return;
    }

    try {
      const messages = await fetchSupportMessages();
      setUnreadSupportMessagesCount(countUnreadSupportMessagesByLastSeen(messages));

      if (messages.length > 0) {
        setSupportMessage(getLatestActiveSupportMessage(messages));
        setIsSupportChatOpen(true);
        writeSupportLastSeenNow();
        setUnreadSupportMessagesCount(0);
        return;
      }

      const defaultMessage = language === 'ua'
        ? 'Хочу звернутися до підтримки.'
        : 'I want to contact support.';

      const createResponse = await fetch('/api/v1/support/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          category: 'technical',
          message: defaultMessage,
        }),
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create support message');
      }

      const created = await createResponse.json();

      const messagesAfterCreate = await fetchSupportMessages();
      setUnreadSupportMessagesCount(countUnreadSupportMessagesByLastSeen(messagesAfterCreate));
      const fullMessage = messagesAfterCreate.find((m: any) => m.id === created?.id) || getLatestActiveSupportMessage(messagesAfterCreate);

      setSupportMessage(fullMessage);
      setIsSupportChatOpen(true);
      writeSupportLastSeenNow();
      setUnreadSupportMessagesCount(0);
    } catch (error) {
      console.error('Failed to open support chat:', error);
    }
  };

  const handleAdminReply = async (messageId: string, reply: string) => {
    try {
      const response = await fetch(`/api/v1/admin/support/messages/${messageId}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({ reply }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send admin reply');
      }

      // Refresh messages
      const messages = await fetchSupportMessages();
      if (user?.role === 'super_admin') {
        setUnreadClientMessagesForAdmin(countUnreadClientMessagesForAdmin(messages));
      } else {
        setUnreadSupportMessagesCount(countUnreadSupportMessagesByLastSeen(messages));
      }
      const updated = supportMessage
        ? messages.find((m: any) => m.id === supportMessage?.id) || getLatestActiveSupportMessage(messages)
        : getLatestActiveSupportMessage(messages);
      setSupportMessage(updated);
    } catch (error) {
      console.error('Failed to send admin reply:', error);
      throw error;
    }
  };

  const handleGlobalClick = () => {
    console.log('Open global/world menu');
    // TODO: Implement global functionality (language, region, etc.)
  };

  const handleSearchClick = () => {
    console.log('Open search');
    // TODO: Implement search functionality
  };

  const handleLogoutClick = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <header className="header-menu">
        <div className="header-left">
          {/* Optional: breadcrumbs or page title can be added here */}
        </div>

        <div className="header-right">
          {user?.role !== 'client' && (
            <button
              className="icon-btn"
              aria-label="Пошук"
              onClick={handleSearchClick}
              title="Пошук"
            >
              <i className="bi bi-search"></i>
            </button>
          )}

          {user?.role !== 'client' && (
            <button
              className="icon-btn"
              aria-label="Глобальні налаштування"
              onClick={handleGlobalClick}
              title="Перегляд сайту"
            >
              <i className="bi bi-globe"></i>
            </button>
          )}


          <Tooltip title={t('navigation.tooltips.supportChat')} arrow>
            <button
              className="icon-btn"
              aria-label="Повідомлення"
              onClick={handleSupportChatClick}
            >
              <Badge
                badgeContent={(() => {
                  const count = user?.role === 'super_admin' ? unreadClientMessagesForAdmin : unreadSupportMessagesCount;
                  //console.log('[HeaderMenu] Badge rendering - role:', user?.role, 'count:', count);
                  return count;
                })()}
                color="primary"
                invisible={user?.role === 'super_admin' ? unreadClientMessagesForAdmin === 0 : unreadSupportMessagesCount === 0}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    minWidth: '16px',
                    height: '16px',
                  },
                }}
              >
                <i className="bi bi-chat-dots"></i>
              </Badge>
            </button>
          </Tooltip>

          {isAuthenticated ? (
            <>
              <button
                className="profile-btn"
                aria-label={t('navigation.actions.profile')}
                onClick={handleProfileClick}
                title={t('navigation.actions.profile')}
              >
                <span className="profile-avatar" aria-hidden="true">
                  <i className="bi bi-person-circle"></i>
                </span>
                <span className="profile-info-text">
                  <span className="profile-name">{user?.fullName || t('navigation.actions.profileShort')}</span>
                  {user?.role && (
                    <span className="profile-role">{user.role}</span>
                  )}
                </span>
              </button>
              <button
                className="icon-btn"
                aria-label={t('navigation.actions.logout')}
                onClick={handleLogoutClick}
                title={t('navigation.actions.logout')}
              >
                <LogoutIcon />
              </button>
            </>
          ) : (
            <button
              className="icon-btn"
              aria-label={t('public.header.login')}
              onClick={onLoginClick}
              title={t('public.header.login')}
            >
              <i className="bi bi-box-arrow-in-right"></i>
            </button>
          )}
        </div>
      </header>

      <SupportChatModal
        open={isSupportChatOpen}
        message={supportMessage}
        onClose={() => {
          setIsSupportChatOpen(false);
          setSupportMessage(null);
          if (user?.role === 'super_admin') {
            writeAdminLastSeenNow();
            setUnreadClientMessagesForAdmin(0);
          } else {
            writeSupportLastSeenNow();
            setUnreadSupportMessagesCount(0);
          }
        }}
        onMessageSent={async () => {
          try {
            const messages = await fetchSupportMessages();
            if (user?.role === 'super_admin') {
              setUnreadClientMessagesForAdmin(countUnreadClientMessagesForAdmin(messages));
            } else {
              setUnreadSupportMessagesCount(countUnreadSupportMessagesByLastSeen(messages));
            }
            const updated = supportMessage
              ? messages.find((m: any) => m.id === supportMessage?.id) || getLatestActiveSupportMessage(messages)
              : getLatestActiveSupportMessage(messages);
            setSupportMessage(updated);
            if (user?.role === 'super_admin') {
              writeAdminLastSeenNow();
              setUnreadClientMessagesForAdmin(0);
            } else {
              writeSupportLastSeenNow();
              setUnreadSupportMessagesCount(0);
            }
          } catch (error) {
            console.error('Failed to refresh support messages:', error);
          }
        }}
        isAdmin={user?.role === 'super_admin'}
        onSendReply={user?.role === 'super_admin' ? handleAdminReply : undefined}
      />
    </>
  );
};

export default HeaderMenu;
